"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileSearch, SlidersHorizontal as Sliders, MapPin, Star, Code as Code2, ChartLine as LineChart, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const steps = [{
  number: "01",
  title: "Technical & Content Audit",
  icon: FileSearch,
  badge: "Step 1",
  description: "I personally uncover hidden crawl errors, indexation roadblocks, and content gaps holding your website back from top rankings.",
  details: ["In-depth technical health check", "Content cannibalisation analysis", "Competitor positioning breakdown"]
}, {
  number: "02",
  title: "Strategic On-Page Optimization",
  icon: Sliders,
  badge: "Step 2",
  description: "I refine your page architecture, search intent alignment, and high-value keyword targets for maximum organic search relevance.",
  details: ["Intent-driven content mapping", "Semantic metadata optimization", "Internal linking distribution"]
}, {
  number: "03",
  title: "Authority & Citation Building",
  icon: MapPin,
  badge: "Step 3",
  description: "I strengthen your local and industry footprint with consistent business citations and high-trust, contextual link signals.",
  details: ["NAP consistency verifications", "Niche-relevant directory citations", "High-authority outreach strategy"]
}, {
  number: "04",
  title: "Reputation & Review Growth",
  icon: Star,
  badge: "Step 4",
  description: "I set up sustainable review loops that enhance local trust signals, Google Business Profile prominence, and user conversion rates.",
  details: ["Google Business Profile enhancement", "Customer feedback workflows", "Trust signal optimization"]
}, {
  number: "05",
  title: "Schema & Entity Alignment",
  icon: Code2,
  badge: "Step 5",
  description: "I implement precise JSON-LD structured data so search engines and AI engines fully understand your business context and services.",
  details: ["Custom Organization & LocalBusiness schema", "Product & Service entity markup", "AI search engine readability"]
}, {
  number: "06",
  title: "Transparent KPI Reporting",
  icon: LineChart,
  badge: "Step 6",
  description: "No vanity metrics. I deliver clear, monthly video walkthroughs detailing organic revenue growth, qualified leads, and keyword ranks.",
  details: ["Direct consultant video summary", "Conversion & lead tracking", "Iterative growth roadmap"]
}];
export default function Methodology() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="methodologyflow" data-nav="light" className="relative bg-background py-20 md:py-28 overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 flex justify-center">
        <div className="h-[500px] w-[500px] rounded-full bg-secondary/40 blur-3xl opacity-50" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-sm font-medium border border-border">
            Hands-On Process
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            My Proven SEO Methodology for Sustainable Growth
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            When you work with YE Consultancy, you work directly with me—Yassine Essaadi. 
            Here is the step-by-step framework I use to scale search visibility and real business revenue.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 relative">
          {steps.map((step, index) => {
          const IconComponent = step.icon;
          const isLast = index === steps.length - 1;
          return <motion.div key={step.number} variants={itemVariants} data-index={index} className="relative flex flex-col h-full">
                {!isLast && <div aria-hidden="true" className="hidden lg:block absolute -right-5 top-12 z-10 text-accent/80 transform translate-x-1/2">
                    <div className="flex items-center space-x-1">
                      <span className="h-0.5 w-6 bg-accent/40 rounded-full inline-block" />
                      <div className="w-2 h-2 rounded-full bg-accent" />
                    </div>
                  </div>}

                <Card className="flex flex-col h-full bg-card border border-border/80 shadow-md hover:shadow-lg transition-[box-shadow,border-color] duration-300 hover:border-primary/40 relative z-0">
                  <CardHeader className="pt-6 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center justify-center size-12 rounded-xl bg-primary text-primary-foreground font-semibold text-lg shadow-sm">
                        {step.number}
                      </div>

                      <div className="flex items-center justify-center size-10 rounded-lg bg-secondary text-accent">
                        <IconComponent className="size-5" />
                      </div>
                    </div>

                    <CardTitle className="text-xl font-semibold text-card-foreground font-default">
                      {step.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {step.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="mt-auto pt-2 pb-6">
                    <div className="border-t border-border pt-4">
                      <ul className="space-y-2">
                        {step.details.map((detail, dIdx) => <li key={dIdx} className="flex items-start text-xs sm:text-sm text-foreground/90">
                            <CheckCircle2 className="size-4 text-accent shrink-0 mr-2 mt-0.5" />
                            <span>{detail}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-16 md:mt-20 text-center bg-card rounded-2xl p-8 sm:p-10 border border-border shadow-sm max-w-4xl mx-auto">
          <h3 className="text-xl sm:text-2xl font-semibold text-foreground font-default">
            Ready to implement a tailored SEO strategy for your business?
          </h3>
          <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
            Book a direct consultation with me to discuss your website audit, growth strategy, and ranking goals.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto px-6 font-medium">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto px-6 font-medium">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}